import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowDownUp, ChevronLeft, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCryptoIconFallback } from "@/lib/cryptoIcons";
import { formatAssetAmount, toAssetInputString } from "@/lib/amountFormat";
import {
  CASH_COUNTRIES,
  CASH_DEFAULT_COUNTRY,
  CASH_QUICK_AMOUNTS_RUB,
  SUPPORTED_ASSETS,
} from "@/lib/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GIVE_CRYPTO = "crypto_to_cash";
const GIVE_CASH = "cash_to_crypto";
const FIAT = "RUB";
const OTHER_COUNTRY = "__other__";
const OTHER_CITY = "__other_city__";
// ~5 пунктов списка (5×32px + отступы + стрелка скролла), остальное — скроллом.
const SELECT_LIST_MAX_H = "max-h-[194px]";

const formatRub = (digits) => String(digits || "").replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const CashOutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, user, balances, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } =
    useApp();
  const showInlineBack = !isTelegramWebApp;

  const [direction, setDirection] = useState(GIVE_CRYPTO);
  const [cryptoCurrency, setCryptoCurrency] = useState("USDT");
  const [cryptoAmount, setCryptoAmount] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [country, setCountry] = useState(CASH_DEFAULT_COUNTRY);
  const [city, setCity] = useState("");
  const [customCity, setCustomCity] = useState("");
  const [customLocation, setCustomLocation] = useState("");
  const [contact, setContact] = useState("");
  const [contactTouched, setContactTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  const isGiveCash = direction === GIVE_CASH;
  const cryptoIcon = useMemo(
    () => SUPPORTED_ASSETS.find((a) => a.currency === cryptoCurrency)?.icon || "",
    [cryptoCurrency]
  );
  const isOtherCountry = country === OTHER_COUNTRY;
  // Доступный остаток в выбранной монете — для направления «отдаю крипту».
  const availableBalance = useMemo(() => {
    const row = (balances || []).find((b) => b.currency === cryptoCurrency);
    if (!row) return 0;
    const amount = Number(row.amount ?? 0);
    const hold = Number(row.hold ?? 0);
    const available = Number(row.available ?? amount - hold);
    return Number.isFinite(available) ? Math.max(available, 0) : 0;
  }, [balances, cryptoCurrency]);
  const countryCities = useMemo(
    () => CASH_COUNTRIES.find((c) => c.name === country)?.cities || [],
    [country]
  );

  const handleCountryChange = (next) => {
    if (next === country) return;
    setCountry(next);
    setCity("");
    setCustomCity("");
  };

  // Ник из Telegram — только как начальное значение, ручной ввод не перетираем.
  useEffect(() => {
    if (contactTouched) return;
    const tgUsername = String(user?.telegram_username || "").trim();
    if (tgUsername) setContact(`@${tgUsername.replace(/^@/, "")}`);
  }, [user, contactTouched]);

  const giveAmount = isGiveCash ? cashAmount : cryptoAmount;
  const amountNum = parseFloat(String(giveAmount).replace(",", "."));
  const hasValidAmount = Number.isFinite(amountNum) && amountNum > 0;
  // Что уйдёт в заявку как "город": свободная строка при «Другое»,
  // для России — просто город, для остальных стран — "Город, Страна".
  const cityValue = city === OTHER_CITY ? customCity.trim() : city;
  const locationValue = isOtherCountry
    ? customLocation.trim()
    : !cityValue
      ? ""
      : country === CASH_DEFAULT_COUNTRY
        ? cityValue
        : `${cityValue}, ${country}`;
  const insufficientFunds = !isGiveCash && hasValidAmount && amountNum > availableBalance + 1e-9;
  const canSubmit =
    hasValidAmount &&
    !insufficientFunds &&
    locationValue.length >= 2 &&
    contact.trim().length >= 2 &&
    !loading;

  const handleBack = useCallback(() => {
    haptic?.("light");
    navigate(-1);
  }, [haptic, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  const switchDirection = (next) => {
    if (next === direction) return;
    haptic?.("light");
    setDirection(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    haptic?.("medium");
    setLoading(true);
    try {
      await api.post("/cash-request", {
        direction,
        give_amount: amountNum,
        give_currency: isGiveCash ? FIAT : cryptoCurrency,
        get_currency: isGiveCash ? cryptoCurrency : FIAT,
        city: locationValue,
        contact: contact.trim(),
      });
      toast.success(t("cash.submitted", "Заявка отправлена — оператор свяжется с вами"));
      setCryptoAmount("");
      setCashAmount("");
      navigate("/services");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : t("cash.submit_failed", "Не удалось отправить заявку. Попробуйте позже")
      );
    } finally {
      setLoading(false);
    }
  };

  const cryptoSelect = (
    <Select value={cryptoCurrency} onValueChange={setCryptoCurrency} disabled={loading}>
      <SelectTrigger className="w-32 shrink-0" data-testid="cash-crypto-select">
        <div className="flex items-center gap-2">
          <img
            src={cryptoIcon || getCryptoIconFallback(cryptoCurrency)}
            alt={cryptoCurrency}
            className="w-5 h-5 rounded-full"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getCryptoIconFallback(cryptoCurrency);
            }}
          />
          <span>{cryptoCurrency}</span>
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-[7.5rem]">
        {SUPPORTED_ASSETS.map((asset) => (
          <SelectItem key={asset.currency} value={asset.currency}>
            <div className="flex items-center gap-2">
              <img
                src={asset.icon}
                alt={asset.currency}
                className="w-5 h-5 rounded-full"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getCryptoIconFallback(asset.currency);
                }}
              />
              <span>{asset.currency}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const fiatChip = (
    <span className="shrink-0 rounded-md bg-[var(--lp-tile)] px-3 py-2 text-sm font-medium text-foreground">
      ₽ {FIAT}
    </span>
  );

  const operatorCalcRow = (
    <div className="flex items-center justify-between gap-3">
      <span className="text-base text-emerald-600 dark:text-emerald-500">
        {t("cash.operator_calc", "Сумму рассчитает оператор")}
      </span>
      {isGiveCash ? cryptoSelect : fiatChip}
    </div>
  );

  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col animate-fade-in" data-testid="cash-out-page">
      <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
        {showInlineBack ? (
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-8 w-8 items-center justify-center text-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        ) : (
          <span aria-hidden="true" className="h-8 w-8" />
        )}
        <h1 className="text-center text-[17px] font-semibold text-foreground">
          {t("cash.title", "Выдача наличных")}
        </h1>
        <span aria-hidden="true" className="h-8 w-8" />
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-5 pb-[calc(7rem+var(--lp-bottom))] space-y-5">
        {/* Направление обмена */}
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-[var(--lp-tile)] p-1">
          {[
            { key: GIVE_CRYPTO, label: t("cash.tab_give_crypto", "Отдаю крипту") },
            { key: GIVE_CASH, label: t("cash.tab_give_cash", "Отдаю наличные") },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => switchDirection(tab.key)}
              className={`rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                direction === tab.key
                  ? "lp-tile !rounded-lg text-foreground"
                  : "text-muted-foreground"
              }`}
              data-testid={`cash-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Отдаю */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {isGiveCash
              ? t("cash.you_give_cash", "Вы отдаёте наличными")
              : t("cash.you_give", "Вы отдаёте")}
          </p>
          <div
            className={`lp-glass p-4 ${insufficientFunds ? "!border-destructive/60" : ""}`}
          >
            <div className="flex items-center gap-3">
              {isGiveCash ? (
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formatRub(cashAmount)}
                  onChange={(e) => setCashAmount(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="0"
                  className="border-none h-12 p-0 text-3xl font-bold text-foreground focus-visible:ring-0"
                  data-testid="cash-amount-input"
                  disabled={loading}
                />
              ) : (
                <Input
                  type="text"
                  inputMode="decimal"
                  value={cryptoAmount}
                  onChange={(e) =>
                    setCryptoAmount(e.target.value.replace(/[^\d.,]/g, "").slice(0, 20))
                  }
                  placeholder="0.00"
                  className="border-none h-12 p-0 text-3xl font-bold text-foreground focus-visible:ring-0"
                  data-testid="crypto-amount-input"
                  disabled={loading}
                />
              )}
              {isGiveCash ? fiatChip : cryptoSelect}
            </div>
            {!isGiveCash && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (availableBalance <= 0 || loading) return;
                    haptic?.("light");
                    setCryptoAmount(toAssetInputString(availableBalance, cryptoCurrency));
                  }}
                  className="text-xs text-muted-foreground active:opacity-70"
                  data-testid="cash-available-balance"
                  disabled={loading}
                >
                  {t("cash.available", "Доступно")}:{" "}
                  <span className="font-medium text-foreground">
                    {formatAssetAmount(availableBalance, cryptoCurrency)} {cryptoCurrency}
                  </span>
                </button>
                {insufficientFunds && (
                  <span
                    className="flex items-center gap-1 text-xs font-medium text-destructive-fg"
                    data-testid="cash-insufficient-funds"
                  >
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {t("cash.insufficient_funds", "Недостаточно средств")}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Переключить направление */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => switchDirection(isGiveCash ? GIVE_CRYPTO : GIVE_CASH)}
            className="h-10 w-10 rounded-xl bg-primary text-primary-foreground shadow flex items-center justify-center active:scale-95 transition-transform"
            aria-label={t("cash.switch_direction", "Сменить направление")}
            data-testid="cash-switch-direction"
            disabled={loading}
          >
            <ArrowDownUp className="w-4 h-4" />
          </button>
        </div>

        {/* Получаю — считает оператор */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {isGiveCash
              ? t("cash.you_receive", "Вы получаете")
              : t("cash.you_receive_cash", "Вы получаете наличными")}
          </p>
          <div className="lp-glass border-dashed p-4">
            {operatorCalcRow}
          </div>
        </div>

        {/* Быстрые суммы — только для наличных */}
        {isGiveCash && (
          <div className="flex flex-wrap gap-2">
            {CASH_QUICK_AMOUNTS_RUB.map((value) => {
              const active = String(value) === cashAmount;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    haptic?.("light");
                    setCashAmount(String(value));
                  }}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-primary text-link font-medium"
                      : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-foreground"
                  }`}
                  data-testid={`cash-quick-${value}`}
                  disabled={loading}
                >
                  {formatRub(String(value))} ₽
                </button>
              );
            })}
          </div>
        )}

        {/* Страна */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("cash.country", "Страна")}
          </p>
          <Select value={country} onValueChange={handleCountryChange} disabled={loading}>
            <SelectTrigger className="h-14 rounded-2xl" data-testid="cash-country-select">
              <SelectValue placeholder={t("cash.country_placeholder", "Выберите страну")} />
            </SelectTrigger>
            <SelectContent className={SELECT_LIST_MAX_H}>
              {CASH_COUNTRIES.map((item) => (
                <SelectItem key={item.name} value={item.name}>
                  {item.name}
                </SelectItem>
              ))}
              <SelectItem value={OTHER_COUNTRY}>
                {t("cash.country_other", "Другое")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Город */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("cash.city", "Город")}
          </p>
          {isOtherCountry ? (
            <Input
              type="text"
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value.slice(0, 64))}
              placeholder={t("cash.custom_location_placeholder", "Укажите страну и город")}
              className="h-14 rounded-2xl px-4"
              data-testid="cash-custom-location-input"
              disabled={loading}
            />
          ) : (
            <>
              <Select value={city} onValueChange={setCity} disabled={loading}>
                <SelectTrigger className="h-14 rounded-2xl" data-testid="cash-city-select">
                  <SelectValue placeholder={t("cash.city_placeholder", "Выберите город")} />
                </SelectTrigger>
                <SelectContent className={SELECT_LIST_MAX_H}>
                  {countryCities.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                  <SelectItem value={OTHER_CITY}>
                    {t("cash.city_other", "Другое")}
                  </SelectItem>
                </SelectContent>
              </Select>
              {city === OTHER_CITY && (
                <Input
                  type="text"
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value.slice(0, 64))}
                  placeholder={t("cash.custom_city_placeholder", "Укажите город")}
                  className="h-14 rounded-2xl px-4"
                  data-testid="cash-custom-city-input"
                  disabled={loading}
                />
              )}
            </>
          )}
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            {t("cash.city_hint", "Наличие в вашем городе подтвердит оператор")}
          </p>
        </div>

        {/* Контакт */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("cash.contact", "Контакт для связи")}
          </p>
          <Input
            type="text"
            value={contact}
            onChange={(e) => {
              setContactTouched(true);
              setContact(e.target.value.slice(0, 64));
            }}
            placeholder={t("cash.contact_placeholder", "@username в Telegram")}
            className="h-14 rounded-2xl px-4"
            data-testid="cash-contact-input"
            disabled={loading}
          />
        </div>

        <div className="space-y-2 pt-1">
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full h-14 rounded-2xl text-base"
            data-testid="cash-submit-btn"
          >
            {loading && <div className="spinner mr-2" />}
            {insufficientFunds
              ? t("cash.insufficient_funds", "Недостаточно средств")
              : t("cash.submit", "Оставить заявку")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            {t("cash.support_note", "С вами свяжется поддержка ~10 минут")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CashOutPage;
